import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { _, TranslateService } from '@ngx-translate/core';
import { mgcIconMailSendRegular } from '@sb/mgc-icons/mgcIcon-mail_send_regular.icon';
import { SbDialogService } from '@sb/ui/dialog';
import { IconRegistry } from '@sb/ui/icon';
import { take } from 'rxjs';

import { HireInviteSentResult } from '../models/new-hires.model';
import { HireBeginFlowModalComponent } from '../new-hires-overview/add-new-hires-modal/hire-begin-flow-modal.component';
import { RehireBeginFlowModalComponent } from '../new-hires-overview/rehire-modal/rehire-begin-flow-modal.component';

const inviteSentMessage = _(
  "[first_name] has been invited to complete their details. We'll notify you once they've updated their details.",
);
const contractSetupHint = _('In the meantime, you can finish entering the contract information.');

@Injectable({ providedIn: 'root' })
export class NewHireDialogService {
  #dialogService = inject(SbDialogService);
  #router = inject(Router);
  #translateService = inject(TranslateService);

  constructor() {
    // The feedback dialog renders whatever icon name it is handed, and mail-send is outside the
    // base set, so it has to be registered here.
    inject(IconRegistry).registerIcons([mgcIconMailSendRegular]);
  }

  openAddNewHireDialog() {
    return this.#openInviteDialog(HireBeginFlowModalComponent, _('New hire invite sent'));
  }

  openRehireFormerEmployeeDialog() {
    return this.#openInviteDialog(RehireBeginFlowModalComponent, _('Rehire invite sent'));
  }

  #openInviteDialog<T>(modal: ComponentType<T>, sentTitle: string) {
    const dialogRef = this.#dialogService.setSize('lg').open<T, unknown, HireInviteSentResult | undefined>(modal);

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if (result) {
        this.#openInviteSentDialog(sentTitle, result);
      }
    });

    return dialogRef;
  }

  #openInviteSentDialog(sentTitle: string, { newHireId, firstName }: HireInviteSentResult) {
    // The feedback dialog renders `description` as one paragraph, so the break between the two
    // sentences has to travel inside the string.
    const description = [
      this.#translateService.instant(inviteSentMessage, { first_name: firstName }),
      this.#translateService.instant(contractSetupHint),
    ].join('<br><br>');

    this.#dialogService.openFeedback({
      title: this.#translateService.instant(sentTitle),
      description,
      type: 'success',
      icon: 'mail-send-regular',
      secondary: {
        text: this.#translateService.instant("I'll do this later"),
      },
      primary: {
        text: this.#translateService.instant('Fill out contract details'),
        onClick: () => {
          this.#router.navigate([`/hiring/new-hires/${newHireId}`], { queryParams: { step: 'contractSetup' } });
        },
      },
    });
  }
}
